import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { Loader2, User, KeyRound, ShieldCheck, AlertCircle, Clock, CheckCircle2, WifiOff } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LoginPage = () => {
  const { loginWithPassword, loginWithPin, setupAdmin, setupRequired, loading: authLoading, isOffline } = useAuth();
  
  // Admin login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  
  // PIN login state
  const [pin, setPin] = useState("");
  
  // Setup state
  const [setupUsername, setSetupUsername] = useState("");
  const [setupDisplayName, setSetupDisplayName] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setupConfirmPassword, setSetupConfirmPassword] = useState("");
  
  // Clock In/Out state
  const [clockPin, setClockPin] = useState("");
  const [clockResult, setClockResult] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Company logo for the login screen — pulled from public Company Settings
  // endpoint so we don't need an auth token. Falls back gracefully if the
  // backend is unreachable or no logo has been uploaded.
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/company`);
        if (!cancelled) setCompanyLogoUrl(res.data?.logo_url || "");
      } catch (_e) { /* leave blank */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      await loginWithPassword(username, password);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      await loginWithPin(pin);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Invalid PIN");
    } finally {
      setLoading(false);
    }
  };

  const handlePinKeyPress = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      
      // Auto-submit when PIN is 4-6 digits and user presses a key after 4
      if (newPin.length >= 4) {
        // Small delay to show the digit, then try login
        setTimeout(() => {
          handlePinSubmit(newPin);
        }, 200);
      }
    }
  };

  const handlePinSubmit = async (pinValue) => {
    if (pinValue.length < 4) return;
    
    setError("");
    setLoading(true);
    
    try {
      await loginWithPin(pinValue);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const handlePinClear = () => {
    setPin("");
    setError("");
  };

  const handlePinBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  // Clock In/Out handlers
  const handleClockPinSubmit = async (action) => {
    if (clockPin.length !== 4) {
      setError("Please enter your 4-digit PIN");
      return;
    }
    
    setClockLoading(true);
    setError("");
    setClockResult(null);
    
    try {
      const endpoint = action === "in" ? "/employees/clock/in-with-pin" : "/employees/clock/out-with-pin";
      const response = await axios.post(`${API}${endpoint}?pin=${clockPin}`);
      setClockResult({
        success: true,
        action: action,
        ...response.data
      });
      setClockPin("");
    } catch (err) {
      setError(err.response?.data?.detail || `Clock ${action} failed`);
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockPinKeyPress = (digit) => {
    if (clockPin.length < 4) {
      setClockPin(clockPin + digit);
      setError("");
      setClockResult(null);
    }
  };

  const handleClockPinClear = () => {
    setClockPin("");
    setError("");
    setClockResult(null);
  };

  const handleClockPinBackspace = () => {
    setClockPin(clockPin.slice(0, -1));
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError("");
    
    if (setupPassword !== setupConfirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (setupPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await setupAdmin(setupUsername, setupDisplayName, setupPassword);
      // After setup, show login screen
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Initial Setup Screen
  if (setupRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
              <ShieldCheck className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Welcome to POS System</CardTitle>
            <CardDescription>
              Create your admin account to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetup} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="setup-username">Username</Label>
                <Input
                  id="setup-username"
                  value={setupUsername}
                  onChange={(e) => setSetupUsername(e.target.value)}
                  placeholder="admin"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="setup-display">Display Name</Label>
                <Input
                  id="setup-display"
                  value={setupDisplayName}
                  onChange={(e) => setSetupDisplayName(e.target.value)}
                  placeholder="Your Name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="setup-password">Password</Label>
                <Input
                  id="setup-password"
                  type="password"
                  value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="setup-confirm">Confirm Password</Label>
                <Input
                  id="setup-confirm"
                  type="password"
                  value={setupConfirmPassword}
                  onChange={(e) => setSetupConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Admin Account
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {companyLogoUrl && (
            <img
              src={companyLogoUrl}
              alt="Company logo"
              className="mx-auto mb-3 h-20 w-auto max-w-[220px] object-contain bg-white rounded p-2 shadow-sm"
              data-testid="login-company-logo"
            />
          )}
          <CardTitle className="text-2xl font-bold">BOSVELD BOU & HARDEWARE</CardTitle>
          <CardDescription>Point of Sale System</CardDescription>
        </CardHeader>
        <CardContent>
          {isOffline && (
            <div className="mb-4 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-800" data-testid="offline-login-banner">
              <WifiOff className="h-4 w-4 shrink-0" />
              <span>You're offline — login with previously used credentials</span>
            </div>
          )}
          <Tabs defaultValue="pin" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="pin" className="flex items-center gap-1 text-xs sm:text-sm">
                <KeyRound className="h-4 w-4" />
                <span className="hidden sm:inline">Cashier</span> PIN
              </TabsTrigger>
              <TabsTrigger value="clock" className="flex items-center gap-1 text-xs sm:text-sm">
                <Clock className="h-4 w-4" />
                Clock In/Out
              </TabsTrigger>
              <TabsTrigger value="admin" className="flex items-center gap-1 text-xs sm:text-sm">
                <User className="h-4 w-4" />
                Admin
              </TabsTrigger>
            </TabsList>
            
            {/* PIN Login Tab */}
            <TabsContent value="pin" className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={(e) => { e.preventDefault(); handlePinSubmit(pin); }} className="text-center mb-4">
                <p className="text-gray-600 mb-4">Enter your 4-digit PIN</p>
                
                {/* Keyboard Input Field */}
                <div className="mb-4">
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setPin(val);
                      setError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && pin.length >= 4) {
                        e.preventDefault();
                        handlePinSubmit(pin);
                      }
                    }}
                    placeholder="Enter PIN"
                    className="text-center text-2xl tracking-widest font-mono h-14 max-w-[200px] mx-auto"
                    autoFocus
                    disabled={loading}
                  />
                </div>
                
                {/* Visual PIN dots */}
                <div className="flex justify-center gap-2 mb-6">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-10 h-10 border-2 rounded-lg flex items-center justify-center text-xl font-bold
                        ${pin.length > i ? "border-blue-500 bg-blue-50" : "border-gray-300"}`}
                    >
                      {pin.length > i ? "•" : ""}
                    </div>
                  ))}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full max-w-[200px]" 
                  disabled={loading || pin.length < 4}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4 mr-2" />
                  )}
                  Login
                </Button>
              </form>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or use keypad</span>
                </div>
              </div>
              
              {/* PIN Pad */}
              <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <Button
                    key={digit}
                    type="button"
                    variant="outline"
                    className="h-12 text-lg font-semibold"
                    onClick={() => handlePinKeyPress(digit.toString())}
                    disabled={loading}
                  >
                    {digit}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 text-sm"
                  onClick={handlePinClear}
                  disabled={loading}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 text-lg font-semibold"
                  onClick={() => handlePinKeyPress("0")}
                  disabled={loading}
                >
                  0
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 text-sm"
                  onClick={handlePinBackspace}
                  disabled={loading}
                >
                  ←
                </Button>
              </div>
            </TabsContent>
            
            {/* Clock In/Out Tab */}
            <TabsContent value="clock" className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {clockResult && clockResult.success && (
                <Alert className="border-green-500 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <div className="font-semibold">{clockResult.employee_name}</div>
                    <div>{clockResult.message}</div>
                    {clockResult.hours_worked > 0 && (
                      <div className="text-sm mt-1">
                        Hours worked: {clockResult.hours_worked}h
                        {clockResult.overtime_hours > 0 && ` (OT: ${clockResult.overtime_hours}h)`}
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="text-center">
                <p className="text-gray-600 mb-4">Enter your 4-digit PIN to clock in or out</p>
                
                {/* PIN Input */}
                <div className="mb-4">
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={clockPin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setClockPin(val);
                      setError("");
                      setClockResult(null);
                    }}
                    placeholder="Enter PIN"
                    className="text-center text-2xl tracking-widest font-mono h-14 max-w-[200px] mx-auto"
                    disabled={clockLoading}
                  />
                </div>
                
                {/* Visual PIN dots */}
                <div className="flex justify-center gap-2 mb-6">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-10 h-10 border-2 rounded-lg flex items-center justify-center text-xl font-bold
                        ${clockPin.length > i ? "border-green-500 bg-green-50" : "border-gray-300"}`}
                    >
                      {clockPin.length > i ? "•" : ""}
                    </div>
                  ))}
                </div>
                
                {/* Clock In/Out Buttons */}
                <div className="flex gap-3 justify-center mb-6">
                  <Button 
                    onClick={() => handleClockPinSubmit("in")}
                    className="bg-green-600 hover:bg-green-700 px-6"
                    disabled={clockLoading || clockPin.length !== 4}
                  >
                    {clockLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Clock className="h-4 w-4 mr-2" />
                    )}
                    Clock IN
                  </Button>
                  <Button 
                    onClick={() => handleClockPinSubmit("out")}
                    className="bg-orange-600 hover:bg-orange-700 px-6"
                    disabled={clockLoading || clockPin.length !== 4}
                  >
                    {clockLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Clock className="h-4 w-4 mr-2" />
                    )}
                    Clock OUT
                  </Button>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or use keypad</span>
                </div>
              </div>
              
              {/* PIN Pad */}
              <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <Button
                    key={digit}
                    type="button"
                    variant="outline"
                    className="h-12 text-lg font-semibold"
                    onClick={() => handleClockPinKeyPress(digit.toString())}
                    disabled={clockLoading}
                  >
                    {digit}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 text-sm"
                  onClick={handleClockPinClear}
                  disabled={clockLoading}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 text-lg font-semibold"
                  onClick={() => handleClockPinKeyPress("0")}
                  disabled={clockLoading}
                >
                  0
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 text-sm"
                  onClick={handleClockPinBackspace}
                  disabled={clockLoading}
                >
                  ←
                </Button>
              </div>
            </TabsContent>
            
            {/* Admin Login Tab */}
            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    disabled={loading}
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Login
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
